import React, { useMemo, useState } from 'react';

/**
 * Shows edge weight evolution over time — the "online learning" story.
 * A small set of edges; pressing "run another session" nudges weights via EMA.
 */
function LearningLoop() {
  const initial = useMemo(
    () => [
      { from: 'auth', to: 'jwt', w: 0.45 },
      { from: 'auth', to: 'oauth2', w: 0.4 },
      { from: 'auth', to: 'session', w: 0.55 },
      { from: 'auth', to: 'saml', w: 0.5 }, // will decay
      { from: 'auth', to: 'cookie', w: 0.35 },
    ],
    []
  );
  const [edges, setEdges] = useState(initial);
  const [sessions, setSessions] = useState(0);

  // Simulated per-session feedback: which edges are reinforced / decayed.
  const reinforced = ['auth→jwt', 'auth→oauth2', 'auth→session'];
  const decayed = ['auth→saml'];

  const stepOnce = () => {
    const alpha = 0.25;
    setEdges((prev) =>
      prev.map((e) => {
        const key = `${e.from}→${e.to}`;
        if (reinforced.includes(key)) {
          // EMA toward 1.0
          return { ...e, w: +(e.w + alpha * (1.0 - e.w)).toFixed(2) };
        }
        if (decayed.includes(key)) {
          return { ...e, w: +(e.w + alpha * (0.0 - e.w)).toFixed(2) };
        }
        // gentle drift toward 0 (recency decay on unused edges)
        return { ...e, w: +(e.w + alpha * 0.1 * (0 - e.w)).toFixed(2) };
      })
    );
    setSessions((s) => s + 1);
  };

  const reset = () => {
    setEdges(initial);
    setSessions(0);
  };

  return (
    <div className="loop">
      <div
        className="flex justify-between items-center"
        style={{ marginBottom: 20, flexWrap: 'wrap', gap: 12 }}
      >
        <div>
          <div className="demo-label">Edge weights · EMA update</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
            Sessions simulated:{' '}
            <span className="mono" style={{ color: 'var(--ink)' }}>
              {sessions}
            </span>
          </div>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-ghost" onClick={stepOnce} type="button">
            Run a session →
          </button>
          <button
            className="btn btn-ghost"
            onClick={reset}
            style={{ fontSize: 12 }}
            type="button"
          >
            Reset
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {edges.map((e) => {
          const key = `${e.from}→${e.to}`;
          const status = reinforced.includes(key)
            ? { label: 'accepted', color: 'var(--good)' }
            : decayed.includes(key)
            ? { label: 'rejected', color: 'var(--ink-4)' }
            : { label: 'unused', color: 'var(--ink-4)' };
          const pct = Math.max(2, Math.min(100, Math.round(e.w * 100)));
          return (
            <div
              key={key}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr 56px 90px',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <span
                className="mono"
                style={{ fontSize: 12, color: 'var(--ink-2)' }}
              >
                {e.from} <span style={{ color: 'var(--ink-4)' }}>→</span>{' '}
                {e.to}
              </span>
              <div
                style={{
                  height: 6,
                  background: 'var(--bg-2)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid var(--line)',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: reinforced.includes(key)
                      ? 'var(--ink)'
                      : decayed.includes(key)
                      ? 'var(--line-2)'
                      : 'var(--ink-4)',
                    transition: 'width .6s ease, background .4s',
                  }}
                />
              </div>
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {e.w.toFixed(2)}
              </span>
              <span
                className="mono"
                style={{
                  fontSize: 10,
                  color: status.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                · {status.label}
              </span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 20,
          paddingTop: 16,
          borderTop: '1px solid var(--line)',
          fontSize: 12,
          color: 'var(--ink-4)',
          lineHeight: 1.6,
        }}
      >
        When you tell MME a memory was useful, the edge from your query's seed
        tags to that memory gets an EMA nudge toward{' '}
        <span className="mono">1.0</span>. Unused edges decay. No retraining,
        no embeddings — just weights on a graph.
      </div>
    </div>
  );
}

export default LearningLoop;
